import React, { useState } from 'react';
import { Mail, MessageSquare, CheckCircle, Youtube, Play } from 'lucide-react';

const Support = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    // Netlify will handle the actual submission
    // We just show a success message
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="bg-black min-h-screen text-white pt-20 px-4 py-8">
        <div className="max-w-2xl mx-auto text-center py-24">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-4">Message Received!</h1>
          <p className="text-xl text-gray-400 mb-8">
            Thanks for contacting us. We'll get back to you at the email you provided as soon as possible.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="text-green-500 hover:text-green-400"
          >
            Submit another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen text-white pt-20 px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-4">Get Support</h1>
          <p className="text-xl text-gray-400">
            Having an issue or need help? Send us a message and we'll get back to you soon.
          </p>
        </div>

        {/* YouTube Channel Callout */}
        <div className="bg-gradient-to-r from-red-900/20 to-gray-900/50 rounded-lg p-6 border border-red-700/30 mb-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
                <Youtube className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-2">
                Looking for tutorials and workflow examples?
              </h3>
              <p className="text-gray-300 mb-4">
                Check out our YouTube channel for video guides, tips, and workflow demonstrations.
              </p>
              <a
                href="https://www.youtube.com/@ContextUI"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg
                         hover:bg-red-700 transition-colors font-semibold"
              >
                <Play className="w-5 h-5" />
                Visit YouTube Channel
              </a>
            </div>
          </div>
        </div>

        {/* Support Form */}
        <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
          <form
            name="support"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Hidden fields for Netlify */}
            <input type="hidden" name="form-name" value="support" />
            <p className="hidden">
              <label>
                Don't fill this out if you're human: <input name="bot-field" />
              </label>
            </p>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Your Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500
                         transition-colors"
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-2">
                Issue Category *
              </label>
              <select
                id="category"
                name="category"
                required
                className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                         text-white focus:outline-none focus:border-emerald-500
                         transition-colors"
              >
                <option value="">Select a category</option>
                <option value="installation">Installation Issue</option>
                <option value="account">Account Problem</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="6"
                placeholder="Please describe your issue or question..."
                className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-lg
                         text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500
                         transition-colors resize-vertical"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold text-lg
                       hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Send Message
            </button>
          </form>
        </div>

        {/* Alternative Contact */}
        <div className="text-center mt-8 text-gray-400">
          <p>
            Or email us directly at{' '}
            <a href="mailto:support@isd.ai" className="text-green-500 hover:text-green-400">
              support@isd.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Support;